/**
 * Dashboard — user-facing home of the app.
 *
 * Replaces the previous tech-only Dashboard (now at /system/dashboard-tech).
 * Shows what matters to a regular member of the organization (cooperative,
 * association, hub…): a brief org summary, KPIs that adapt to the active
 * features, the latest org-wide announcements, upcoming meetings and a
 * placeholder for the future Budget module.
 *
 * Look & feel matches the rest of the app (stat-card classes, vida colour
 * palette, white cards with rounded borders). Data comes from a single
 * call to /dashboard/overview so the page paints in one render.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  CubeIcon,
  PencilSquareIcon,
  ShoppingCartIcon,
  FolderIcon,
  Squares2X2Icon,
  BellAlertIcon,
  MegaphoneIcon,
  CalendarDaysIcon,
  BanknotesIcon,
  ArrowRightIcon,
  UserGroupIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { Header } from '@/components/Layout';
import { PageLoader } from '@/components/common';
import { dashboardApi, crmApi, type DashboardOverview } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';

function formatEuros(cents: number): string {
  const eur = (cents || 0) / 100;
  return eur.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: 'short' });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

function StatCard({
  title, value, icon: Icon, href, accent = 'gray',
}: {
  title: string;
  value: string | number;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  href?: string;
  accent?: 'gray' | 'vida' | 'amber' | 'emerald';
}) {
  const accentClasses: Record<string, string> = {
    gray:    'bg-gray-50    text-gray-600',
    vida:    'bg-vida-50    text-vida-700',
    amber:   'bg-amber-50   text-amber-700',
    emerald: 'bg-emerald-50 text-emerald-700',
  };
  const inner = (
    <div className="stat-card hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="stat-card-title truncate">{title}</p>
          <p className="stat-card-value truncate">{value}</p>
        </div>
        <div className={`p-2 rounded-lg shrink-0 ${accentClasses[accent]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
  return href ? <Link to={href} className="block">{inner}</Link> : inner;
}

function LevelBadge({ level }: { level?: 'info' | 'warning' | 'critical' }) {
  const map: Record<string, string> = {
    info:     'bg-vida-50 text-vida-700',
    warning:  'bg-amber-50 text-amber-700',
    critical: 'bg-red-50 text-red-700',
  };
  const label: Record<string, string> = { info: 'Informativo', warning: 'Aviso', critical: 'Importante' };
  const k = level || 'info';
  return <span className={`text-xs px-2 py-0.5 rounded-full ${map[k]}`}>{label[k]}</span>;
}

export default function Dashboard() {
  const user = useAuthStore((s) => s.user);
  const selectedOrg = useOrganizationStore((s) => s.selectedOrg);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  // CRM: conteos de clientes y leads (solo si la org tiene el CRM activo).
  const crmOn = selectedOrg?.config?.management?.crm?.enabled === true;
  const [crmCounts, setCrmCounts] = useState<{ customers: number; leads: number } | null>(null);
  useEffect(() => {
    if (!crmOn) { setCrmCounts(null); return; }
    let cancelled = false;
    Promise.all([
      crmApi.listContacts({ type: 'customer', limit: 1 }),
      crmApi.listContacts({ type: 'lead', limit: 1 }),
    ])
      .then(([c, l]) => { if (!cancelled) setCrmCounts({ customers: c.data.total || 0, leads: l.data.total || 0 }); })
      .catch(() => { if (!cancelled) setCrmCounts(null); });
    return () => { cancelled = true; };
  }, [crmOn, selectedOrg?._id]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await dashboardApi.getOverview();
        if (!cancelled) setOverview(r.data?.data || null);
      } catch (e: unknown) {
        if (!cancelled) {
          const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error
            || 'No se pudo cargar el panel';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
    // Reload when the active org changes (different KPIs).
  }, [selectedOrg?._id]);

  if (loading && !overview) return <PageLoader />;

  const firstName = user?.profile?.first_name || 'Hola';
  const orgName = overview?.organization.name || selectedOrg?.name || 'tu organización';
  const features = overview?.organization.features || {};
  const kpis = overview?.kpis || {};
  const announcements = overview?.announcements || [];
  const meetings = overview?.meetings || [];
  const budget = overview?.budget || null;

  // VIDA-294: permisos DE ESTA ORG (no la unión de todas, que es lo que guarda
  // el store de auth y lo que hacía que el menú enseñara secciones con 403).
  const orgPerms = overview?.permissions || [];
  const canHere = (p: string) => orgPerms.includes('*') || orgPerms.includes(p);
  const welcomeText = overview?.organization.welcome_text || '';

  // El backend ya omite los KPIs que el usuario no puede ver, así que basta con
  // comprobar si llegó alguno para decidir si la sección de cifras tiene sentido.
  const visibleKpis = [
    kpis.users, kpis.members, kpis.catalogProducts, kpis.blogPostsPublished,
    kpis.ordersThisMonth, kpis.projectsActive, kpis.processesActive,
  ].filter((v) => v !== undefined).length;

  // Accesos directos a lo que este usuario SÍ puede abrir. Para un miembro con
  // pocos permisos esto es prácticamente todo su panel.
  const quickLinks = [
    { perm: 'cms:read', label: 'Contenido web', href: '/pet' },
    { perm: 'blog:read', label: 'Blog', href: '/blog' },
    { perm: 'catalog:read', label: 'Catálogo', href: '/catalog' },
    { perm: 'products:read', label: 'Productos', href: '/products' },
    { perm: 'ecommerce:read', label: 'Ecommerce', href: '/ecommerce' },
    { perm: 'crm:read', label: 'CRM', href: '/crm/contacts' },
    { perm: 'members:read', label: 'Asociados', href: '/members' },
    { perm: 'fees:read', label: 'Cuotas', href: '/fees' },
    { perm: 'budget:read', label: 'Presupuesto', href: '/budget' },
    { perm: 'planner:read', label: 'Planificador', href: '/planner' },
    { perm: 'processes:read', label: 'Procesos', href: '/processes' },
    { perm: 'meetings:read', label: 'Reuniones', href: '/meetings' },
  ].filter((l) => canHere(l.perm));

  return (
    <div>
      <Header
        title={`Hola, ${firstName}`}
        subtitle={`Resumen de ${orgName}`}
      />

      <div className="p-6 space-y-6">
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* ---------------- Bienvenida de la organización ---------------- */}
        {welcomeText && (
          <section className="rounded-xl border border-vida-200 bg-vida-50 p-4">
            <p className="whitespace-pre-line text-sm text-gray-800">{welcomeText}</p>
          </section>
        )}

        {/* ---------------- KPIs adaptativos ----------------
            Solo se pintan los que el backend devolvió, es decir, aquellos cuyo
            permiso tiene el usuario en ESTA organización (VIDA-294). */}
        {visibleKpis > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Cifras de la organización
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {kpis.users !== undefined && (
              <StatCard
                title="Usuarios"
                value={kpis.users}
                icon={UsersIcon}
                href="/users"
                accent="vida"
              />
            )}
            {crmOn && (
              <>
                <StatCard
                  title="Clientes"
                  value={crmCounts?.customers ?? '—'}
                  icon={UserGroupIcon}
                  href="/crm/contacts?type=customer"
                  accent="vida"
                />
                <StatCard
                  title="Leads"
                  value={crmCounts?.leads ?? '—'}
                  icon={FunnelIcon}
                  href="/crm/contacts?type=lead"
                  accent="amber"
                />
              </>
            )}
            {kpis.members !== undefined && (
              <StatCard
                title="Asociados"
                value={kpis.members}
                icon={UsersIcon}
                href="/members"
                accent="vida"
              />
            )}
            {kpis.catalogProducts !== undefined && (
              <StatCard
                title="Productos"
                value={kpis.catalogProducts}
                icon={CubeIcon}
                href="/catalog"
              />
            )}
            {kpis.blogPostsPublished !== undefined && (
              <StatCard
                title="Posts publicados"
                value={kpis.blogPostsPublished}
                icon={PencilSquareIcon}
                href="/blog"
              />
            )}
            {kpis.ordersThisMonth !== undefined && (
              <>
                <StatCard
                  title="Pedidos del mes"
                  value={kpis.ordersThisMonth}
                  icon={ShoppingCartIcon}
                  href="/ecommerce"
                  accent="emerald"
                />
                <StatCard
                  title="Facturado del mes"
                  value={formatEuros(kpis.revenueThisMonthCents ?? 0)}
                  icon={BanknotesIcon}
                  href="/ecommerce"
                  accent="emerald"
                />
              </>
            )}
            {kpis.projectsActive !== undefined && (
              <>
                <StatCard
                  title="Proyectos activos"
                  value={kpis.projectsActive}
                  icon={FolderIcon}
                  href="/projects"
                />
                <StatCard
                  title="Alarmas próximas (7 días)"
                  value={kpis.upcomingAlarms7d ?? 0}
                  icon={BellAlertIcon}
                  href="/planner"
                  accent="amber"
                />
              </>
            )}
            {kpis.processesActive !== undefined && (
              <StatCard
                title="Procesos en curso"
                value={kpis.processesActive}
                icon={Squares2X2Icon}
                href="/processes"
              />
            )}
            <StatCard
              title="Comunicados sin leer"
              value={kpis.unreadAnnouncements ?? 0}
              icon={MegaphoneIcon}
              accent={kpis.unreadAnnouncements ? 'amber' : 'gray'}
            />
          </div>
        </section>
        )}

        {/* ---------------- Accesos del usuario ----------------
            Lo que ESTE usuario puede abrir en ESTA organización. Para un miembro
            con pocos permisos, esto sustituye al panel de cifras que no puede ver. */}
        {quickLinks.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Tus accesos
            </h2>
            <div className="flex flex-wrap gap-2">
              {quickLinks.map((l) => (
                <Link
                  key={l.href}
                  to={l.href}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:border-vida-300 hover:text-vida-700"
                >
                  {l.label}
                  <ArrowRightIcon className="w-3.5 h-3.5" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ---------------- Comunicados / Noticias ---------------- */}
          <section className="lg:col-span-2 bg-white rounded-xl border border-gray-200">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <MegaphoneIcon className="w-5 h-5 text-vida-600" />
                <h2 className="text-base font-semibold text-gray-900">Comunicados y noticias</h2>
              </div>
              <Link to="/marketing" className="text-sm text-vida-600 hover:underline inline-flex items-center gap-1">
                Ver todos <ArrowRightIcon className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="divide-y divide-gray-100">
              {announcements.length === 0 ? (
                <p className="p-6 text-sm text-gray-500 text-center">
                  No hay comunicados publicados todavía. Los anuncios que se publiquen desde
                  <strong> Marketing → Anuncios</strong> aparecerán aquí.
                </p>
              ) : (
                announcements.map((a) => (
                  <article key={a._id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-gray-900">{a.title}</h3>
                      <LevelBadge level={a.level} />
                    </div>
                    {a.body && <p className="text-sm text-gray-600 line-clamp-3 whitespace-pre-line">{a.body}</p>}
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      <span>{formatDate(a.createdAt)}</span>
                      {a.publicWeb && <span className="inline-flex items-center gap-1 text-vida-600">· También en la web</span>}
                      {a.url && (
                        <a href={a.url} className="text-vida-600 hover:underline" target="_blank" rel="noreferrer">
                          Abrir →
                        </a>
                      )}
                    </div>
                  </article>
                ))
              )}
            </div>
          </section>

          <div className="space-y-6">
            {/* ---------------- Próximas reuniones ----------------
                Solo para quien pueda verlas: sin planner:read el backend no
                devuelve reuniones y el estado vacío sería engañoso. */}
            {canHere('planner:read') && (
            <section className="bg-white rounded-xl border border-gray-200">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <CalendarDaysIcon className="w-5 h-5 text-vida-600" />
                  <h2 className="text-base font-semibold text-gray-900">Próximas reuniones</h2>
                </div>
                {features.planner && (
                  <Link to="/planner" className="text-sm text-vida-600 hover:underline inline-flex items-center gap-1">
                    Calendario <ArrowRightIcon className="w-3.5 h-3.5" />
                  </Link>
                )}
              </div>
              <div className="divide-y divide-gray-100">
                {!features.planner ? (
                  <p className="p-6 text-sm text-gray-500 text-center">
                    Activa el <strong>Planificador</strong> en la organización para programar y ver reuniones aquí.
                  </p>
                ) : meetings.length === 0 ? (
                  <p className="p-6 text-sm text-gray-500 text-center">
                    No hay reuniones programadas. Crea una nueva desde el <Link to="/planner" className="text-vida-600 hover:underline">Planificador</Link> seleccionando el tipo <em>reunión</em>.
                  </p>
                ) : (
                  meetings.map((m, i) => (
                    <div key={i} className="p-4">
                      <p className="text-sm font-semibold text-gray-900">
                        {m.title || m.notes?.split('\n')[0] || 'Reunión'}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formatDate(m.start)} · {formatTime(m.start)}–{formatTime(m.end)}
                        {m.location && <> · {m.location}</>}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </section>
            )}

            {/* ---------------- Presupuesto (datos reales del modulo Gestion) ---------------- */}
            {budget && (
              <section className="bg-white rounded-xl border border-gray-200">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <BanknotesIcon className="w-5 h-5 text-vida-600" />
                    <h2 className="text-base font-semibold text-gray-900">Presupuesto {budget.year}</h2>
                  </div>
                  <Link to="/budget" className="text-sm text-vida-600 hover:underline inline-flex items-center gap-1">
                    Ver completo <ArrowRightIcon className="w-3.5 h-3.5" />
                  </Link>
                </div>
                <div className="p-4 space-y-4">
                  {/* Saldo */}
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Saldo del ejercicio</p>
                    <p className={`text-2xl font-bold ${budget.balanceCents >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {budget.balanceCents >= 0 ? '+' : ''}{formatEuros(budget.balanceCents)}
                    </p>
                  </div>

                  {/* Ejecucion ingresos */}
                  {budget.plannedIncomeCents > 0 && (
                    <div>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-500">Ingresos</span>
                        <span className="font-medium text-gray-700">
                          {formatEuros(budget.actualIncomeCents)}{' '}
                          <span className="text-gray-400">/ {formatEuros(budget.plannedIncomeCents)}</span>
                        </span>
                      </div>
                      <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-vida-500"
                          style={{ width: `${Math.min(100, Math.round((budget.actualIncomeCents / budget.plannedIncomeCents) * 100))}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {Math.round((budget.actualIncomeCents / budget.plannedIncomeCents) * 100)}% del previsto
                      </p>
                    </div>
                  )}

                  {/* Ejecucion gastos */}
                  {budget.plannedExpenseCents > 0 && (
                    <div>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-500">Gastos</span>
                        <span className="font-medium text-gray-700">
                          {formatEuros(budget.actualExpenseCents)}{' '}
                          <span className="text-gray-400">/ {formatEuros(budget.plannedExpenseCents)}</span>
                        </span>
                      </div>
                      <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-500"
                          style={{ width: `${Math.min(100, Math.round((budget.actualExpenseCents / budget.plannedExpenseCents) * 100))}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {Math.round((budget.actualExpenseCents / budget.plannedExpenseCents) * 100)}% del previsto
                      </p>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
